<template>
  <div
    v-if="showProcessingFeeBanner && isProcessingFeeStartsInFuture"
    class="col-12"
  >
    <div class="processing-fee-banner-merchant">
      <sb-banner
        v-show="processingFeeBannerVisible"
        variation="warning"
      >
        <strong>
          <span>
            {{ $t('payment-ui.processing-fee-future-start-message', { processingFesStartsInDays: processingFesStartsInDays, processingFee: processingFeeConfiguration.fee}) }}
          </span>
          {{ $t('payment-ui.avoid-processing-fee') }}
          <a
            :href="`${SUPPORT_ARTICLE_LINK.SettingUpPaymentThroughAchBankAccount}`"
            target="_blank"
            rel="noopener" class="credit-card-fees-anchor"
          >{{ $t('click-here') }}</a>
        </strong>
        <span class="buttons-container">
          <span>
            <sb-button class="switch-to-bank-button" @click="redirectToPaymentUi"> {{ $t('payment-ui.switch-to-bank-account-ach') }} </sb-button>
          </span>
          <span>
            <sb-button variation="minimal" @click="displayAcknowledgementPopup">{{ $t('payment-ui.continue-using-credit-card') }}</sb-button>
          </span>
        </span>
      </sb-banner>
    </div>

    <sb-modal
      v-if="displayPopup"
      :active="true"
      :escape-trigger="false"
      :click-outside-trigger="false"
      class="cc-link-card-modal"
      @close="closePopup"
    >
      <section
        class="processing-fee-confirmation"
      >
        <h1 class="text-center confirmation-header">
          {{ $t('payment-ui.processing-fee-modal-header') }}
        </h1>
        <sb-checkbox
          v-model="isConfirmChecked"
        >
          {{ $t('payment-ui.processing-fee-acknowledge-checkbox-text', { processingFee: processingFeeConfiguration.fee, processingFeeStartDate: processingFeeStartDate }) }}
        </sb-checkbox>
        <sb-button
          id="submit"
          class="card-confirmation-btn"
          :disabled="!isConfirmChecked"
          @click="acknowledgeProcessingFee"
        >
          {{ $t('payment-ui.fee-modal-yes-i-am-sure') }}
        </sb-button>
        <br>
        <sb-anchor
          @click="closePopup"
        >
          {{ $t('payment-ui.fee-modal-cancel') }}
        </sb-anchor>
      </section>
      <sb-loading-spinner v-show="showLoading" />
    </sb-modal>
  </div>
</template>

<script>
import CardTypeEnum from '@/enums/modules/payment/cardTypeEnum'
import PaymentMethodStatusEnum from '@/enums/modules/payment/paymentMethodStatusEnum'
import CountryEnum from '@/enums/modules/payment/countryEnum'
import { SUPPORT_ARTICLE_LINK } from '@/constant/supportArticle'
import toastr from 'toastr'

export default {
  name: 'CreditCardProcessingFeeBanner',
  directives: {},
  filters: {},
  mixins: [],
  props: {},
  data: () => ({
    currencyCode: null,
    processingFeeBannerVisible: false,
    homeCountry: {},
    SUPPORT_ARTICLE_LINK,
    displayPopupTriggered: false,
    isConfirmChecked: false,
    showLoading: false
  }),
  computed: {
    enableNewPaymentUI: function () {
      return this.$shipBobStore.getters.enableNewPaymentUI
    },
    isCreditCardActivePaymentMethod: function () {
      const creditCards = this.$shipBobStore.getters['payment/userPaymentMethods']?.cardPaymentMethods
      return creditCards?.some((x) => x.paymentMethodStatus === PaymentMethodStatusEnum.Active && x.cardType === 'Credit')
    },
    processingFeeStartDate: function () {
      const startDate = new Date(this.processingFeeConfiguration?.startDate)
      const month = startDate.toLocaleString('default', { month: 'long' })
      return `${month} ${startDate.getDate()}, ${startDate.getFullYear()}`
    },
    displayPopup: function () {
      return !this.processingFeeConfiguration?.isAcknowledged && this.displayPopupTriggered
    },
    processingFeeConfiguration () {
      const processingFeesList = this.$shipBobStore.getters['payment/cardProcessingFeeConfiguration']
      return processingFeesList.find(processingFees => processingFees.cardTypeId === CardTypeEnum.Credit) || processingFeesList[0]
    },
    isProcessingFeeStartsInFuture () {
      const currentDate = new Date()
      const currentUtcDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate()))
      return (new Date(this.processingFeeConfiguration?.startDate) > currentUtcDate)
    },
    processingFesStartsInDays () {
      const currentDate = new Date()
      const currentUtcDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate()))
      const processingFeeStartDate = new Date(this.processingFeeConfiguration?.startDate)
      const diff = processingFeeStartDate.getTime() - currentUtcDate.getTime()
      return (diff / (1000 * 60 * 60 * 24)).toFixed(0)
    },
    showProcessingFeeBanner: function () {
      return this.enableNewPaymentUI && (!this.processingFeeConfiguration?.isAcknowledged) && this.processingFeeConfiguration?.fee && this.processingFeeBannerVisible && this.isCreditCardActivePaymentMethod && this.hasUSHomeCountry
    },
    hasUSHomeCountry: function () {
      return this.homeCountry?.countryCode === CountryEnum.US
    }
  },
  i18n: {
    messages: {
      'en-US': {
        'payment-ui.processing-fee-future-start-message': 'In {processingFesStartsInDays} days, all credit card payments will incur a {processingFee}% processing fee on each transaction.',
        'payment-ui.processing-fee-acknowledge-checkbox-text': 'By checking this box, you authorize ShipBob to charge a processing fee of {processingFee}% for each transaction starting on {processingFeeStartDate}.',
        'payment-ui.processing-fee-modal-header': 'Are you sure you want to continue using your credit card?',
        'payment-ui.avoid-processing-fee': 'To avoid processing fees, switch your payment method to a bank account (ACH). To learn more, ',
        'payment-ui.acknowledge-processing-fee-success': 'Thank you for acknowledging the processing fees.',
        'click-here': 'click here.',
        'payment-ui.fee-modal-yes-i-am-sure': 'Yes, I\'m Sure',
        'payment-ui.fee-modal-cancel': 'Cancel',
        'payment-ui.switch-to-bank-account-ach': 'Switch to Bank Account (ACH)',
        'payment-ui.continue-using-credit-card': 'Continue Using Credit Card'
      }
    }
  },
  watch: {},
  async created () {
    this.homeCountry = this.$shipBobStore.getters['pricing/userHomeCountry']
    this.currencyCode = this.homeCountry?.currencyCode
    await this.getProcessingFeeConfig()
    await this.$shipBobStore.dispatch('payment/getUserPaymentMethods')
  },
  methods: {
    async acknowledgeProcessingFee () {
      this.showLoading = true
      await this.$shipBobStore.dispatch('payment/acknowledgeCreditCardProcessingFee', CardTypeEnum.Credit).then(() => {
        this.processingFeeBannerVisible = false
        toastr.success('Thank you for acknowledging the processing fees.')
        this.closePopup()
        this.redirectToPaymentUi()
      }).catch(() => {
        toastr.error('Failed to acknowledge the processing fees. Please try again.')
        this.showLoading = false
      })
    },
    closePopup () {
      this.showLoading = false
      this.displayPopupTriggered = false
    },
    async displayAcknowledgementPopup () {
      this.displayPopupTriggered = true
    },
    redirectToPaymentUi () {
      const url = '/app/merchant/#/Payment'
      window.open(url, '_self')
    },
    async getProcessingFeeConfig () {
      await this.$shipBobStore.dispatch('payment/setCardProcessingFeeConfiguration', { currencyCode: this.currencyCode })
      this.processingFeeBannerVisible = true
    }
  }
}
</script>

<style lang='scss'>
.processing-fee-banner-merchant {
  margin: 0.5rem 1rem;

  .buttons-container {
    display: block;
    margin-top: 10px;

    .switch-to-bank-button {
      margin-right: 15px;
    }
  }
}
.credit-card-fees-anchor {
  color: #2470af;
  text-decoration: none;
}
.cc-link-card-modal {
  z-index: 3;
  .processing-fee-confirmation {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 465px;
  }
  .confirmation-header {
    line-height: 37px;
    margin-bottom: 12px;
  }
  .card-confirmation-btn {
    margin-top: 38px;
  }
  .sb-close-button {
    position: absolute;
  }
}
</style>
